import { LayerWrapper } from '../LayerWrapper';
import { Ajax } from '@shared/Ajax';
import Feature from 'ol/Feature';
import VectorSource from 'ol/source/Vector';
import { removeFeaturesBySource } from '../ol-utils';
import VectorLayer from 'ol/layer/Vector';
import { LineString, MultiPolygon, Point as OlPoint, Point } from 'ol/geom';
import * as olStyle from 'ol/style';
import { makeObservable, observable } from 'mobx';
import { PipeFeatureFactory } from '@openlayers/pipes/PipeFeatureFactory';
import Layer from 'ol/layer/Layer';
import WebGLVectorLayerRenderer from 'ol/renderer/webgl/VectorLayer';
import { GtPointsLayer } from '@openlayers/points/GtPointsLayer';
import { transform as olTransform } from 'ol/proj';
import { isCrashProneMobile } from '@shared/utils/utils';

class ArrowsWebGLLayer extends Layer {
    createRenderer() {
        return new WebGLVectorLayerRenderer(this, {
            style: {
                'fill-color': [0, 0, 0],
            },
            variables: {},
        });
    }
}

export class StringlinesLayer extends LayerWrapper<IOutlinePolyline> {
    featureType: FeatureType = 'stringline';
    overlayModelFeaturesById: { [key: string]: Feature[] } = {};

    // arrows to show elevation of each segment of stringline
    arrowsLayer = new ArrowsWebGLLayer({
        source: new VectorSource() as any,
    });
    arrowsLayerAdded = false;
    arrowFeaturesBySource: Record<string, Feature[]> = {};
    arrowSize: number | null = null;

    // customizable labels that show properties for each line
    @observable lineLabelProps: string[] = [];
    @observable noNamesInLabel = false;
    lineLabelsLayerAdded = false;
    lineLabelsLayerSource: VectorSource = new VectorSource();
    lineLabelsLayer: VectorLayer<any> = new VectorLayer({
        source: this.lineLabelsLayerSource,
        updateWhileAnimating: false,
        updateWhileInteracting: false,
        minZoom: 18,
    });

    // option to treat start/end points in stringline as physical but invisible point
    // used to show property label for each point that makes the line
    // uses gtPointsLayer because it already has labels handling and to reduce number of layers
    @observable showLinePoints = false;
    gtPointsLayer: GtPointsLayer = null;

    constructor(opts, gtPointsLayer: GtPointsLayer) {
        //opts.layerBuilderFn = () => new WebGLLayer({}) // works but no hit detection for now
        super(opts);
        makeObservable(this);
        this.gtPointsLayer = gtPointsLayer;

        // arrow vectors
        const arrowsSource = this.arrowsLayer.getSource() as VectorSource;

        this.arrowsLayer.setZIndex(this.layer.getZIndex() - 1);
        this.arrowsLayer.setVisible(false);

        const addOrRemoveArrowsLayer = _.debounce(() => {
            if (!this.arrowsLayerAdded && !arrowsSource.isEmpty()) {
                this.map.addLayer(this.arrowsLayer);
                this.arrowsLayerAdded = true;
            }
            this.arrowsLayer.setVisible(!arrowsSource.isEmpty());
        }, 10);
        arrowsSource.on('addfeature', addOrRemoveArrowsLayer);
        arrowsSource.on('removefeature', addOrRemoveArrowsLayer);

        // line labels
        this.layerSource.on('addfeature', this.drawLineLabels);
        this.layerSource.on('removefeature', this.drawLineLabels);

        // we need to draw physical (static size) arrows for performance reasons
        // so redraw at few predefined zoom levels so they dont get too big
        setTimeout(() => {
            if (isCrashProneMobile()) {
                this.arrowSize = 0.5;
            } else {
                this.map.getView().on('change:resolution', _.throttle(this.onZoomChange, 100));
                this.onZoomChange();
            }
        }, 1);
    }

    drawAllFromOutlineLayers(layers: IOutlineLayer[], filename?: string) {
        const stringlines: IOutlinePolyline[] = _.flatten(
            layers.map(
                (
                    layer // take only non-solid stringlines from each layer
                ) =>
                    layer.polylines
                        .filter(pl => !pl.solid)
                        .map(stringline => ({
                            ...stringline,
                            modelId: layer.id,
                            filename, // cant use layer.name, its not always equal to filename
                        }))
            )
        );

        const featuresResult = this.drawAll(stringlines);

        this.drawLinePoints();

        // elevation arrows - add new for featuresResult, preserve others/old
        const batchArrowFeatures: Record<string, Feature[]> = {};
        featuresResult.features.forEach(f => {
            if (f.get('showElevationArrow')) {
                const sourceKey = f.get('source')?.modelId ?? -1;
                if (!batchArrowFeatures[sourceKey]) {
                    batchArrowFeatures[sourceKey] = [];
                }
                batchArrowFeatures[sourceKey].push(f);
            }
        });
        Object.assign(this.arrowFeaturesBySource, batchArrowFeatures);

        this.redrawArrows();

        return stringlines;
    }

    onZoomChange = () => {
        let newArrowSize = null;
        const zoom = this.map.getView().getZoom();
        if (zoom >= 24) newArrowSize = 0.2;
        else if (zoom >= 22.5) newArrowSize = 0.4;
        else if (zoom >= 21) newArrowSize = 1;
        else if (zoom >= 20) newArrowSize = 1.5;
        else if (zoom >= 19) newArrowSize = 2;
        else if (zoom >= 17) newArrowSize = 4;

        if (newArrowSize !== this.arrowSize) {
            this.arrowSize = newArrowSize;
            this.redrawArrows();
        }
    };

    redrawArrows = _.debounce(() => {
        const arrowsSource = this.arrowsLayer.getSource() as VectorSource;
        arrowsSource.clear();

        if (!this.arrowSize) {
            return;
        }

        Object.entries(this.arrowFeaturesBySource).forEach(([sourceKey, features]) => {
            const allArrowCoords: number[][][][] = [];
            features.forEach(f => {
                allArrowCoords.push(...PipeFeatureFactory.makeArrowLineCoordinates(f, this.arrowSize));
            });
            if (allArrowCoords.length > 0) {
                const arrowFeature = new Feature({
                    geometry: new MultiPolygon(allArrowCoords),
                });
                arrowFeature.set('source', {
                    modelId: sourceKey,
                });
                arrowsSource.addFeature(arrowFeature);
            }
        });
    }, 10);

    linePointsProcessed = new WeakSet<Feature>();
    drawLinePoints = _.debounce(() => {
        // draw fake points for at the start/end of stringlines for showing points labels on map
        // check if real point already exists because gt files can contain both lines and points at the end of lines

        if (!this.showLinePoints) return;

        const stringlines = this.layerSource.getFeatures();
        if (!stringlines?.length) return;

        const existingRealPoints = this.gtPointsLayer.layerSource
            .getFeatures()
            .filter(f => !f.get('isLinePoint'))
            .map(f => {
                const point = f.getGeometry() as Point;
                return `${point.getCoordinates()[0]}_${point.getCoordinates()[1]}`;
            });

        const sourceModelId = stringlines[0].get('source').modelId;

        const fakePoints: IOutlinePoint[] = stringlines
            .flatMap(f => {
                if (this.linePointsProcessed.has(f)) return [];
                this.linePointsProcessed.add(f);

                const coords = (f.getGeometry() as LineString).getCoordinates();
                const neStart = [...coords[0], f.get('zStart')];
                const neEnd = [...coords[coords.length - 1], f.get('zEnd')];

                return [neStart, neEnd].map(point => {
                    if (existingRealPoints.some(ep => ep === `${point[0]}_${point[1]}`)) {
                        return null;
                    }
                    const latLon = olTransform([point[0], point[1]], 'EPSG:3857', 'EPSG:4326').reverse();
                    return {
                        sourceModelId,
                        isLinePoint: true,
                        lat: latLon[0],
                        lon: latLon[1],
                        elevation: point[2],
                        northing: point[0],
                        easting: point[1],
                        filename: null,
                        code: null,
                        valid: true,
                        props: null,
                    } satisfies IOutlinePoint;
                });
            })
            .filter(Boolean);

        const res = this.gtPointsLayer.drawAll(fakePoints);
        res.features.forEach(f => {
            // numeric 0/1 because the gt points webgl style filter reads it as a float attribute
            f.set('hidden', 1);
            f.set('isLinePoint', true);
            f.set('showLabel', true);
        });
    }, 100);

    removeFeaturesBySource(source: FeatureSource, silent: boolean = true) {
        removeFeaturesBySource(this.arrowsLayer.getSource() as VectorSource, source, false);
        removeFeaturesBySource(this.gtPointsLayer.layerSource as VectorSource, source, false);
        delete this.arrowFeaturesBySource[source.modelId];
        super.removeFeaturesBySource(source, silent);
    }

    removeOverlayModel = (modelId: number) => {
        for (let i = 0, ilen = this.overlayModelFeaturesById[modelId].length; i < ilen; i++) {
            const feature: Feature = this.overlayModelFeaturesById[modelId][i];
            this.layerSource.removeFeature(feature);
        }
        delete this.overlayModelFeaturesById[modelId];
    };

    toggleOverlayModel = _.debounce((modelId: number) => {
        if (this.overlayModelFeaturesById[modelId]) {
            this.removeOverlayModel(modelId);
        } else {
            if ($.isNumeric(modelId)) {
                Ajax({
                    url: `/api/1/file/${modelId}/stringlines.json`,
                }).then(
                    (resp: IStringlinesAjaxResponse) => {
                        const stringlines = this.drawAll(resp.stringlines)?.features;
                        const success = stringlines;
                        this.overlayModelFeaturesById[modelId] = stringlines;
                        return success ? $.Deferred().resolve(resp) : $.Deferred().reject(resp);
                    },
                    resp => $.Deferred().reject(resp, status, 'ajax error')
                );
            }
        }
    }, 50);

    getOverlayModelIds = (): number[] => {
        return Object.keys(this.overlayModelFeaturesById).map(parseFloat);
    };

    setLineLabelProps = (props: string[]) => {
        this.lineLabelProps = Array.isArray(props) ? props : [props];
        this.drawLineLabels();
    };

    setNoNamesInLabels = (value: boolean) => {
        this.noNamesInLabel = value;
        this.drawLineLabels();
    };

    toggleLineLabelProp = (prop: string) => {
        if (this.lineLabelProps.includes(prop)) {
            this.lineLabelProps = this.lineLabelProps.filter(p => p !== prop);
        } else {
            this.lineLabelProps.push(prop);
        }
        this.drawLineLabels();
    };

    addOrRemoveLineLabelsLayer = () => {
        if (this.lineLabelsLayerSource.isEmpty() || this.lineLabelProps.length === 0) {
            if (this.lineLabelsLayerAdded) {
                this.map.removeLayer(this.lineLabelsLayer);
                this.lineLabelsLayerAdded = false;
            }
        } else {
            if (!this.lineLabelsLayerAdded) {
                this.map.addLayer(this.lineLabelsLayer);
                this.lineLabelsLayer.setZIndex(111);
                this.lineLabelsLayerAdded = true;
            }
        }
    };

    drawLineLabels = _.debounce(() => {
        const allFeatures = this.layerSource.getFeatures();
        const labelFeatures: Feature[] = [];

        allFeatures.forEach(feature => {
            const geometry = feature.getGeometry();

            if (geometry && geometry.getType() === 'LineString') {
                const lineGeometry = geometry as LineString;
                const coordinates = lineGeometry.getCoordinates();
                const centerCoord = lineGeometry.getCoordinateAt(0.5);
                if (coordinates.length > 1 && coordinates[0][0] === coordinates[1][0] && coordinates[0][1] === coordinates[1][1]) {
                    return;
                }

                let angle = Math.atan2(coordinates[1][1] - coordinates[0][1], coordinates[1][0] - coordinates[0][0]);
                if (Math.abs(angle) > Math.PI / 2) {
                    angle += angle > 0 ? -Math.PI : Math.PI;
                }

                const pointFeature = new Feature({
                    geometry: new OlPoint(centerCoord),
                });

                const props = feature.get('data').props;
                const propsLower = Object.entries(props).reduce((acc, [key, value]) => {
                    acc[key.toLowerCase()] = value;
                    return acc;
                }, {});
                const text = this.lineLabelProps
                    .map(prop => {
                        const val = propsLower[prop.toLowerCase()];
                        if (val == null) return null;
                        return this.noNamesInLabel ? String(val) : prop + ': ' + val;
                    })
                    .filter(Boolean)
                    .slice(0, 5)
                    .join(', ');

                if (!text) {
                    return;
                }

                pointFeature.setStyle(
                    new olStyle.Style({
                        text: new olStyle.Text({
                            text,
                            font: '15px arial',
                            fill: new olStyle.Fill({ color: '#000' }),
                            stroke: new olStyle.Stroke({ color: '#fff', width: 1 }),
                            offsetY: -15,
                            textAlign: 'center',
                            rotation: -angle,
                            rotateWithView: true,
                        }),
                    })
                );

                labelFeatures.push(pointFeature);
            }
        });

        this.lineLabelsLayerSource.clear();
        if (labelFeatures.length) {
            this.lineLabelsLayerSource.addFeatures(labelFeatures);
        }
        this.addOrRemoveLineLabelsLayer();
    }, 100);

    toggleShowPoints = () => {
        this.setShowPoints(!this.showLinePoints);
    };

    setShowPoints = (show: boolean) => {
        this.showLinePoints = show;
        this.drawLinePoints();
        this.gtPointsLayer.layerSource.forEachFeature(f => {
            if (f.get('isLinePoint')) {
                f.set('showLabel', show);
            }
        });
        this.gtPointsLayer?.layerSource.dispatchEvent('change');
    };

    logStats = () => {
        console.log('stringlines', this.layerSource.getFeatures().length);
        console.log('arrow lines', (this.arrowsLayer.getSource() as VectorSource).getFeatures().length);
        console.log('line labels', this.lineLabelsLayerSource.getFeatures().length);
    };

    setLayerOpacity = (opacity: number) => {
        if (typeof opacity === 'number') {
            opacity = utils.clamp(0.04, opacity / 100, 1);
            this.layer.setOpacity(opacity);
            this.arrowsLayer.setOpacity(opacity);
            this.lineLabelsLayer.setOpacity(opacity);
            this.layerOpacity = opacity;
        } else {
            this.layer.setOpacity(1);
            this.arrowsLayer.setOpacity(1);
            this.lineLabelsLayer.setOpacity(1);
            this.layerOpacity = 100;
        }
    };
}
